import { GENDER } from './config';
import { normalizeFetuses } from './pregnancyNormalize';

// The birth transition (REQ-I4/I5/I6, decision 7): what a newborn person inherits from the fetus
// record and from its mother when a pregnancy is converted to a live birth, and how to navigate
// between the two afterwards. Pure so the inheritance rule — the part with clinical and privacy
// consequences — is pinned by tests rather than only reachable through the dialog.

// A Date → the app's stored date-of-birth string. `dob` is a plain 'YYYY-MM-DD' String on the
// member (with a separate `dobPrecision`), not a Date, so the value has to be formatted. UTC parts,
// to match partialDate and avoid a timezone shifting the day.
export const toIsoDay = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = String(d.getUTCFullYear()).padStart(4, '0');
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// DECISION 7 — the newborn inherits the mother's SURNAME and ADDRESS, and nothing else.
//
// Identifiers (Medicare / NHS number / MRN / hospital number) are deliberately NOT inherited: the
// baby's own are issued after birth, and copying the mother's would attach the baby's later results
// and billing to HER record (REQ-I8). Blank is the correct state until the real number exists.
// The mother's first name is not inherited either; the child's comes from the FETUS where one was
// recorded — see inheritFromFetus (TGCLOUD-1617 item 7).
//
// One list, so a site that later wants to extend what is carried over changes it in one place and
// the tests state exactly what crosses the boundary.
export const MOTHER_INHERITED_FIELDS = Object.freeze([
  'lastName',
  'address_line1',
  'address_line2',
  'address_city',
  'address_post',
  'address_country'
]);

export const inheritFromMother = (mother) => {
  const out = {};
  if (!mother) return out;
  MOTHER_INHERITED_FIELDS.forEach((field) => {
    const value = mother[field];
    // Only carry real values — an inherited '' would overwrite nothing but adds noise to the diff
    // the audit trail shows for the new person.
    if (value != null && value !== '') out[field] = value;
  });
  return out;
};

// What the FETUS itself contributes to the person it becomes: its name (TGCLOUD-1617 item 7), its
// date of birth (REQ-I4) and its clinical diagnoses (REQ-I5 — the whole point of recording a prenatal
// diagnosis is that it follows the child). `dobPrecision` is 'full' because a birth date recorded
// against a fetus is a known day.
export const inheritFromFetus = (fetus) => {
  const out = {};
  if (!fetus) return out;
  // The ticket: "if Name has been provided in Fetus record please include as First Name in Person
  // record in addition to Mum's Last Name as Last Name." Only a real name crosses — an unnamed fetus
  // must leave the field blank rather than write '' over it. `pedigreeSymbolText` is deliberately NOT
  // used as a fallback: it is a chart annotation ("Twin A"), not a person's name.
  const firstName = typeof fetus.name === 'string' ? fetus.name.trim() : '';
  if (firstName) out.firstName = firstName;
  const dob = toIsoDay(fetus.dob);
  if (dob) {
    out.dob = dob;
    out.dobPrecision = 'full';
    out.estimatedDob = false;
  }
  if (Array.isArray(fetus.diagnoses) && fetus.diagnoses.length) {
    // Copied, not referenced — the fetus record keeps its own history, and the child's diagnoses
    // are then edited as that person's (a shared array would let one edit rewrite the other).
    out.diagnoses = fetus.diagnoses.map((d) => ({ ...d }));
  }
  return out;
};

// The sex to OFFER for the newborn (still overridable in the chooser). Only a sex that was actually
// determined is offered — an unknown fetal sex must not become an asserted sex on a person.
export const newbornDefaultGender = (fetus) =>
  fetus && fetus.fetalSexKnown && fetus.gender && fetus.gender !== GENDER.unknown
    ? fetus.gender
    : null;

// Everything the newborn inherits, in one object ready to spread over the created person.
export const newbornInheritance = (mother, fetus) => ({
  ...inheritFromMother(mother),
  ...inheritFromFetus(fetus)
});

// A member matches a stored member reference (_id | uid | pid) — the same three-way match the render
// derivation uses, because a just-added child has a uid but no _id until the pedigree is saved.
export const matchesMemberRef = (member, ref) =>
  ref != null &&
  member != null &&
  (String(member._id) === String(ref) ||
    String(member.uid) === String(ref) ||
    String(member.pid) === String(ref));

// REQ-I6 direction 1: pregnancy → the person born of it.
export const findBornChild = (memberRecords, fetusOrRecord) => {
  const ref = fetusOrRecord && fetusOrRecord.bornMemberId;
  if (ref == null) return null;
  return (memberRecords || []).find((m) => matchesMemberRef(m, ref)) || null;
};

// REQ-I6 direction 2: person → the pregnancy record they were born of, wherever it lives. Scans
// every carrier's records (a person has exactly one birth, so the first hit is the answer) and
// returns the carrier too, since that is who the record hangs off.
export const findBirthRecordFor = (memberRecords, member) => {
  if (!member) return null;
  for (const carrier of memberRecords || []) {
    for (const record of carrier.pregnancies || []) {
      const fetuses = normalizeFetuses(record);
      const index = fetuses.findIndex((f) =>
        matchesMemberRef(member, f && f.bornMemberId)
      );
      if (index !== -1) {
        return { carrier, record, fetus: fetuses[index], fetusIndex: index };
      }
    }
  }
  return null;
};
